import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Card, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDay,
  faCheck,
  faCircleExclamation,
  faClockRotateLeft,
  faListCheck,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../api/API";
import { useAuth } from "../../hooks/useAuth";
import { useWorkspace } from "../../context/WorkspaceContext";
import { addDays, toYMD } from "../../utils/DateUtils";
import { compareTaskPriority, getTaskPriorityClass, getTaskPriorityLabel } from "../../utils/taskPriority";
import "./MyTodayTasksCard.css";

const parseCollection = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (response?.data?.status === false) {
    throw new Error(response?.data?.message || "Nao foi possivel carregar as tarefas.");
  }
  return response?.data?.records || response?.data?.data || response?.data?.items || [];
};

const getUserId = (user) => user?.id ?? user?.userId ?? user?.id_usuario ?? null;

const getTaskDueYmd = (task) => {
  if (!task?.vencimento) return "";
  return String(task.vencimento).slice(0, 10);
};

const sortTasks = (left, right) => {
  const leftDue = getTaskDueYmd(left);
  const rightDue = getTaskDueYmd(right);
  if (leftDue !== rightDue) return leftDue.localeCompare(rightDue);
  return compareTaskPriority(left, right);
};

const MyTodayTasksCard = ({ selectedDate, refreshKey }) => {
  const { user } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const userId = getUserId(user);
  const todayYmd = useMemo(() => toYMD(selectedDate || new Date()), [selectedDate]);
  const yesterdayYmd = useMemo(() => toYMD(addDays(selectedDate || new Date(), -1)), [selectedDate]);

  const loadTasks = useCallback(async () => {
    if (!selectedWorkspaceId || !userId) {
      setTasks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const response = await api.get(`/tasks?workspaceId=${selectedWorkspaceId}`);
      const records = parseCollection(response);
      setTasks(records);
    } catch (error) {
      console.error("MyTodayTasksCard: erro ao carregar tarefas", error);
      setTasks([]);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel carregar suas tarefas."
      );
    } finally {
      setLoading(false);
    }
  }, [selectedWorkspaceId, userId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks, todayYmd, refreshKey]);

  const visibleTasks = useMemo(() => {
    return tasks
      .filter((task) => {
        if (task?.concluido) return false;
        if (task?.id_tarefa !== null && task?.id_tarefa !== undefined) return false;
        if (String(task?.id_usuario_responsavel || "") !== String(userId || "")) return false;
        const dueYmd = getTaskDueYmd(task);
        return dueYmd === todayYmd || dueYmd === yesterdayYmd;
      })
      .sort(sortTasks);
  }, [tasks, todayYmd, yesterdayYmd, userId]);

  const counts = useMemo(
    () => ({
      yesterday: visibleTasks.filter((task) => getTaskDueYmd(task) === yesterdayYmd).length,
      today: visibleTasks.filter((task) => getTaskDueYmd(task) === todayYmd).length,
    }),
    [visibleTasks, todayYmd, yesterdayYmd]
  );

  async function completeTask(task) {
    setTasks((current) =>
      current.map((item) => (item.id === task.id ? { ...item, concluido: true } : item))
    );

    try {
      await api.put(`/tasks/${task.id}`, {
        fields: {
          concluido: true,
          id_workspace: task.id_workspace || selectedWorkspaceId,
        },
      });
    } catch (error) {
      console.error("MyTodayTasksCard: erro ao concluir tarefa", error);
      setTasks((current) =>
        current.map((item) => (item.id === task.id ? { ...item, concluido: task.concluido } : item))
      );
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel concluir a tarefa."
      );
    }
  }

  const renderDueBadge = (task) => {
    const dueYmd = getTaskDueYmd(task);
    if (dueYmd === yesterdayYmd) {
      return (
        <Badge className="my-tasks-due yesterday">
          <FontAwesomeIcon icon={faClockRotateLeft} /> Ontem
        </Badge>
      );
    }

    return (
      <Badge className="my-tasks-due today">
        <FontAwesomeIcon icon={faCalendarDay} /> Hoje
      </Badge>
    );
  };

  return (
    <Card className="my-today-tasks-card border-0 shadow-sm">
      <Card.Body>
        <div className="my-tasks-header">
          <div>
            <h5>
              <FontAwesomeIcon icon={faListCheck} /> Minhas tarefas para hoje
            </h5>
            <small>
              {visibleTasks.length} pendente(s) entre ontem e hoje
            </small>
          </div>
          <div className="my-tasks-counter">
            <span>{counts.yesterday} ontem</span>
            <strong>{counts.today} hoje</strong>
          </div>
        </div>

        {errorMessage && <Alert variant="warning" className="mt-3">{errorMessage}</Alert>}

        {loading ? (
          <div className="my-tasks-loading">
            <Spinner size="sm" />
          </div>
        ) : visibleTasks.length === 0 ? (
          <div className="my-tasks-empty">
            <FontAwesomeIcon icon={faCheck} />
            <strong>Nenhuma tarefa pendente para ontem ou hoje.</strong>
            <span>Seu foco esta livre para o planejamento do dia.</span>
          </div>
        ) : (
          <div className="my-tasks-list">
            {visibleTasks.map((task) => (
              <article key={task.id} className="my-task-row">
                <button
                  type="button"
                  className="my-task-check"
                  onClick={() => completeTask(task)}
                  aria-label={`Concluir ${task.descricao}`}
                  title="Concluir tarefa"
                >
                  <FontAwesomeIcon icon={faCheck} />
                </button>

                <div className="my-task-content">
                  <div className="my-task-title-row">
                    <strong>{task.descricao}</strong>
                    {renderDueBadge(task)}
                  </div>
                  <div className="my-task-meta">
                    <span>{task.projeto || "Sem projeto"}</span>
                    <span className={`my-task-priority ${getTaskPriorityClass(task.prioridade)}`}>
                      <FontAwesomeIcon icon={faCircleExclamation} /> {getTaskPriorityLabel(task.prioridade)}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default MyTodayTasksCard;
